import { ReactComponent as IconUploadInfo } from "@/assets/svgs/arrow-up-tray.svg";
import { ReactComponent as IconCheck } from "@/assets/svgs/check.svg";
import { ReactComponent as IconChevronRight } from "@/assets/svgs/chevron-right-black.svg";
import { ReactComponent as ETRFileUploadIcon } from "@/assets/svgs/etr-file-upload-Icon-Badge.svg";
import { ReactComponent as IconDocumentLine } from "@/assets/svgs/icon-document-line.svg";
import { ReactComponent as IconPhoto } from "@/assets/svgs/icon-photo.svg";
import { ReactComponent as IconCloseGrey } from "@/assets/svgs/iconCloseGrey.svg";
import Toast2 from '@/components/Toast2';
import generateFileTypeErrorMessage from "@/utils/generate-file-type-error-message.js";
import classname from "classnames";
import { uniqueId } from "lodash";
import PropTypes from 'prop-types';
import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import { useDropzone } from 'react-dropzone';
import AttachmentViewer from "../attachments/components/attachment-viewer/attachmentViewer.jsx";
import { ValidatePayloadFunction, ReadCSV } from '@/pages/shared-data/share-data-functions';
import { findDuplicateRecords } from "@/pages/record-transfer/etr-issuance/utils.js";

const ETRFileUploadComponent = React.forwardRef(({
    fileLists,
    setFileLists,
    isReadOnly = true,
    maxFileSize = 5000000, /** Default 5MB in bytes */
    maxFileSizeLabel = "5MB",
    uploadMessage = "You may upload multiple attachments at a time.",
    multiple = true,
    accept = 'application/pdf',
    acceptedFileTypes = ['.pdf'],
    validateOnUpload = false,
    validationSchema = null,
    onValidationError = null,
    onValidationResult = null,
    uniqueKeys = [],
    showApiSpecUrl = true
}, ref) => {    
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    const [scrollPosition, setScrollPosition] = useState(0);
    const contentRef = useRef(null);
    const holderRef = useRef(null);
    const attachmentViewerRef = useRef(null);

    const validateCSVFile = useCallback(async (file) => {
        if (!validateOnUpload || !validationSchema || !file.name.toLowerCase().endsWith('.csv')) {
            return { isValid: true, errors: [] };
        }

        try {
            const itemSchema = validationSchema && validationSchema.type === 'array' && validationSchema.items
                ? validationSchema.items
                : validationSchema;

            const json = await ReadCSV(file, itemSchema, true);

            const csvText = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target.result);
                reader.onerror = (e) => reject(e);
                reader.readAsText(file);
            });

            const validationResult = ValidatePayloadFunction(
                json,
                itemSchema,
                true,
                csvText,
                '',
                showApiSpecUrl
            );


            let errors = [];
            if (!validationResult.isValid) {
                if (validationResult.structureErrorMessage) {
                    errors.push({
                        row: 'File',
                        field: 'Structure',
                        message: validationResult.structureErrorMessage,
                        value: null
                    });
                } else if (validationResult.errors && Array.isArray(validationResult.errors)) {
                    validationResult.errors.forEach((errorGroup, index) => {
                        if (errorGroup?.errors) {
                            Object.keys(errorGroup.errors).forEach(fieldKey => {
                                const fieldMessages = errorGroup.errors[fieldKey];
                                if (Array.isArray(fieldMessages)) {
                                    fieldMessages.forEach(message => {
                                        const rowMatch = message.match(/Row (\d+)/);
                                        const rowNumber = rowMatch ? Number.parseInt(rowMatch[1]) : index + 1;
                                        let cleanMessage = message;

                                        cleanMessage = cleanMessage.replace(/^[^:]+:\s*/, '');

                                        errors.push({
                                            row: rowNumber,
                                            field: fieldKey || 'Unknown',
                                            message: cleanMessage,
                                            value: null
                                        });
                                    });
                                }
                            });
                        } else {
                            console.warn('Unexpected errorGroup structure:', errorGroup);
                            errors.push({
                                row: index + 1,
                                field: 'Unknown',
                                message: errorGroup?.message || 'Validation error',
                                value: null
                            });
                        }
                    });
                }
            }

            const result = {
                isValid: validationResult.isValid,
                errors: errors,
                totalRows: json ? json.length : 0,
                parsedData: json
            };

            return result;

        } catch (parseError) {
            return {
                isValid: false,
                errors: [{
                    row: 'File',
                    field: 'CSV Processing',
                    message: 'Error processing CSV: ' + parseError.message,
                    value: null
                }],
                totalRows: 0
            };
        }
    }, [validateOnUpload, validationSchema]);

    const [attachmentIndex, setAttachmentIndex] = useState(0);

    useImperativeHandle(ref, () => {
        return {
            handlePrint
        };
    }, []);

    function handlePrint() {
        attachmentViewerRef?.current?.handlePrint();
    }

    const handleRightClick = () => {
        if (contentRef.current) {
            const containerWidth = contentRef.current?.scrollWidth - contentRef.current?.offsetWidth;
            const newScrollPos = scrollPosition - 100;
            setScrollPosition(Math.abs(newScrollPos) > containerWidth ? -containerWidth : newScrollPos);
        }
    };

    const handleLeftClick = () => {
        const newScrollPos = scrollPosition + 100;
        setScrollPosition(Math.min(0, newScrollPos));
    };

    const file_name_button__wrapper = classname({
        "file_name_button__wrapper": true,
        "file_name_button__wrapper-right_border": scrollPosition - holderRef.current?.offsetWidth + contentRef.current?.scrollWidth > 0,
        "file_name_button__wrapper-left_border": scrollPosition !== 0,
    })

    const onDrop = useCallback(async (acceptedFiles) => {
        const newFiles = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }));

        const oversizedFile = newFiles?.find(el => el.size > maxFileSize);
        if (oversizedFile) {
            setError(`File uploaded size is more than ${maxFileSizeLabel}`);
            return;
        }

        if (validateOnUpload && validationSchema) {
            if (onValidationResult) {
                onValidationResult({
                    isValid: true,
                    errors: [],
                    file: null,
                    parsedData: null
                });
            }
            setError("");
        }

        let validFiles = newFiles;
        if (validateOnUpload && validationSchema) {
            validFiles = [];
            for (const file of newFiles) {
                if (file.name.toLowerCase().endsWith('.csv')) {
                    const validationResult = await validateCSVFile(file);
                    if (!validationResult.isValid) {
                        if (onValidationResult) {
                            onValidationResult({
                                isValid: false,
                                errors: validationResult.errors,
                                file: file,
                                parsedData: validationResult.parsedData
                            });
                        }

                        if (onValidationError) {
                            onValidationError(validationResult.errors, file);
                        }
                    } else {
                        const duplicateErrors = findDuplicateRecords(
                            validationResult.parsedData,
                            uniqueKeys ?? []
                        );
                    
                        if (duplicateErrors && duplicateErrors.length > 0) {
                            validationResult.isValid = false;
                            validationResult.errors = [...validationResult.errors, ...duplicateErrors];
                                if (onValidationResult) {
                                    onValidationResult({
                                        isValid: false,
                                        errors: duplicateErrors,
                                        file: file,
                                        parsedData: null
                                    })
                                }
                        } else {
                            setError("");
                            if (onValidationResult) {
                                onValidationResult({
                                    isValid: true,
                                    errors: [],
                                    file: null,
                                    parsedData: validationResult.parsedData
                                });
                            }
                            validFiles.push(file);
                        }
                        
                    }
                } else {
                    validFiles.push(file);
                }
            }
        }

        setFileLists(oldFiles => {
            if (!multiple) {
                const result = validFiles.slice(0, 1);
                return result;
            }

            const checkedOldFiles = oldFiles?.filter(oldFile => !validFiles.find(validFile => validFile.name === oldFile.name));
            const result = checkedOldFiles.concat(validFiles);
            return result;
        });

        setRefreshKey((prev) => prev + 1);
    }, [maxFileSize, maxFileSizeLabel, multiple, validateOnUpload, validationSchema, validateCSVFile, onValidationError, onValidationResult]);

    const onDropRejectedAttachment = useCallback(_rejectedFiles => {
        return setError(generateFileTypeErrorMessage(acceptedFileTypes));
    }, [acceptedFileTypes]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        onDropRejected: onDropRejectedAttachment,
        multiple,
        accept
    });

    function removeUploadedFile(fileName) {
        const data = fileLists?.filter((item) => !item.name.includes(fileName));
        setFileLists(data);
        setRefreshKey((prev) => (prev + 1));

        if (onValidationResult) {
            onValidationResult({
                isValid: true,
                errors: [],
                file: null,
                parsedData: null
            });
        }
    }

    function onAddAttachment(e) {
        e.stopPropagation();
        document.getElementById(`input-file`).click();
    }

    return (
        <>
            {!isReadOnly && <div className="eform-row" data-testid="eform-row" {...getRootProps()}>
                <div className="etr-issuance-record-data-tip-flex">
                    <div className="etr-data-element-payload-heading-details-left">
                        <IconUploadInfo />
                        <div className="etr-issuance-record-list-tip-details">{uploadMessage}</div>
                    </div>
                    <div className="etr-data-element-payload-heading-details-right">
                        <div
                            className="etr-data-element-payload-clear-attachments"
                            data-testid="eform-clear-all-attachments"
                            onClick={(e) => {
                                e.stopPropagation();
                                setFileLists([]);
                                setRefreshKey((prev) => (prev + 1));

                                if (onValidationResult) {
                                    onValidationResult({
                                        isValid: true,
                                        errors: [],
                                        file: null,
                                        parsedData: null
                                    });
                                }
                            }}
                        >
                            Clear Attachments
                        </div>
                    </div>
                </div>
                <div
                    className="eform-attachments-drop etr-eform-margin-bottom"
                    data-testid="eform-attachments-drop"
                >
                    + Drop your attachment here or <span
                        className="eform-attachments-drop-browse"
                        onClick={(e) => onAddAttachment(e)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onAddAttachment(e) }}
                        style={{ cursor: 'pointer' }}
                        role="button"
                        tabIndex="0"
                    >browse</span>.
                </div>
                <input
                    type="file"
                    className="hidden"
                    id={`input-file`}
                    key={refreshKey}
                    data-testid="eform-input-fileupload"
                    {...getInputProps()}
                ></input>
                <div className="eform-file-upload-text">
                    Maximum {maxFileSizeLabel} per file.
                </div>
            </div>}
            {!isReadOnly && fileLists?.length > 0 && (
                <div className="eform-file-upload-attachments-lists">
                    {fileLists?.map((file) => {
                        return (
                            file.name && (
                                <div className={`etr-eform-file-upload-attachment-data`}>
                                    <div><ETRFileUploadIcon className="etr-eform-file-upload-attachments-icon-svg" /></div>
                                    <div className="etr-eform-file-upload-attachments-heading">{file.name}</div>
                                    <div className="tooltip">
                                        <IconCloseGrey
                                            className="etr-eform-file-upload-attachments-heading-svg"
                                            data-testid="eform-remove-file-icon"
                                            onClick={() => { removeUploadedFile(file.name) }}
                                        />
                                        <div className="tooltiptext eform-tooltip-text">
                                            {"Remove"}
                                        </div>
                                    </div>
                                </div>
                            )
                        )
                    })}
                </div>
            )}
            {isReadOnly && fileLists?.length > 0 && (<>
                <div className="etr-file_container">
                    <div className="file_name_button__controller file_name_button__controller--left-button">
                        <IconChevronRight
                            onClick={handleLeftClick}
                            data-testid="icon-left-button"
                        />
                    </div>
                    <div className={file_name_button__wrapper} ref={holderRef}>
                        <div className="file_name_button_container" data-testid="file_name_button_container" style={{ transform: `translateX(${scrollPosition}px)` }} ref={contentRef}>
                            {fileLists?.map((file, index) => {

                                const fileExtension = file?.name.split(".").pop();
                                const id = uniqueId();
                                const currentSelected = attachmentIndex === index;

                                const fileNameButtonClass = classname({
                                    "etr-file_name_button": true,
                                    "etr-selected_file_name_button": currentSelected
                                })

                                return (
                                    <span className={fileNameButtonClass}
                                        data-testid={"attachment-button"}
                                        data-tooltip-id="tooltip"
                                        data-tooltip-content={file.name}
                                        key={id}
                                        onClick={() => {
                                            setAttachmentIndex(index);
                                        }}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                            setAttachmentIndex(index);
                                        }}
                                        role="none"
                                    >
                                        <span className="icon__span">
                                            {currentSelected && <IconCheck />}
                                            {!currentSelected && (fileExtension === "jpg" || fileExtension === "jpeg" || fileExtension === "png") && <IconPhoto />}
                                            {!currentSelected && !(fileExtension === "jpg" || fileExtension === "jpeg" || fileExtension === "png") && <IconDocumentLine />}
                                        </span>
                                        <div className="file_name_button__text">
                                            {file.name}
                                        </div>
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                    <div className="file_name_button__controller file_name_button__controller--right-button">
                        <IconChevronRight
                            onClick={handleRightClick}
                            data-testid="icon-right-button"
                        />
                    </div>
                </div>
                <AttachmentViewer
                    ref={attachmentViewerRef}
                    selectedAttachment={fileLists?.[attachmentIndex]}
                />
            </>)}
            <Toast2 show={error} errorMessage={error} title={"Invalid File"} warningIcon={true} close={() => { setError("") }} />
        </>
    );
});

ETRFileUploadComponent.propTypes = {
    fileLists: PropTypes.array,
    setFileLists: PropTypes.func,
    isReadOnly: PropTypes.bool,
    maxFileSize: PropTypes.number,
    maxFileSizeLabel: PropTypes.string,
    uploadMessage: PropTypes.string,
    multiple: PropTypes.bool,
    accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    acceptedFileTypes: PropTypes.array,
    validateOnUpload: PropTypes.bool,
    validationSchema: PropTypes.object,
    onValidationError: PropTypes.func,
    onValidationResult: PropTypes.func,
    uniqueKeys: PropTypes.array
};

export default ETRFileUploadComponent;
